/* ═══════════════════════════════════════════════════════════════════════════
   A BUSINESS SIGNS UP

   The one button on the front page addressed to the other side of the
   marketplace, and until now the only one that opened nothing.

   ── Low key, on purpose ───────────────────────────────────────────────────

   Four fields: what you are called, what you do, a mobile number and an email
   address. Everything else a business page needs — the address, the opening
   hours, the org.no, the pictures, the price list — the tenant already has a
   console to fill in, and asking for all of it at the door is how a sign-up
   form becomes a form nobody finishes.

   What that means is that a business exists before its page is ready, and the
   honest way to handle that is to say so: `missing()` below answers what is
   still needed, and `publishable()` is the gate. A page goes public when it
   is a page, not when somebody presses "save".

   ── Which is why the org.no is not asked for here ─────────────────────────

   It IS required before the page is public — a Norwegian business page prints
   it, and the engagement document quotes it. But it is the one field people
   do not know by heart, and asking for it at the door loses sign-ups over a
   number that can be filled in five minutes later from the console.

   **It is never generated.** `orgError` checks the shape and the mod-11 check
   digit, which rejects a typo; it does not invent a number and it does not
   claim the number belongs to this business. Nothing here talks to
   Brønnøysundregistrene, and every screen that shows the field says so.

   ── The money ─────────────────────────────────────────────────────────────

   Signing up costs nothing and this file has no price in it. The
   subscription is a separate act on the tenant's own console, it runs through
   Stripe on Avtale.net's account, and the figures live on the server — see
   domain/billing.js, and the comment in Chamcore's own StripeService that
   says prices must come from the database and not from the frontend.
   ═══════════════════════════════════════════════════════════════════════ */

import { valid as validPhone, normalise } from './phone.js';

/* ── The address on Avtale.net ────────────────────────────────────────────
   Suggested from the name, because a tenant who has to invent a URL at the
   door invents a bad one. It stays editable, and it is checked against the
   platform's own routes before it is offered. */

/** Norwegian letters fold to their ASCII pair, not to nothing: "Frisør"
    becomes "frisor", never "fris r" and never "frisr". */
const FOLD = { æ: 'ae', ø: 'o', å: 'a', ä: 'a', ö: 'o', ü: 'u', é: 'e', è: 'e', ô: 'o' };

export function slugify(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[æøåäöüéèô]/g, c => FOLD[c] || c)
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/&/g, ' og ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
    .replace(/-+$/, '');
}

/**
 * Is this a usable address?
 *
 * `taken` is the list of slugs already in use and `reserved` the platform's
 * own routes — both passed in rather than imported, so this rule can be
 * tested without a catalogue and cannot quietly read one.
 */
export function slugError(slug, { taken = [], reserved = [] } = {}) {
  const s = String(slug || '').toLowerCase();
  if (!s) return 'Pick an address for your page.';
  if (!/^[a-z0-9-]{3,64}$/.test(s))
    return 'An address is 3–64 characters of a–z, 0–9 and hyphen.';
  if (/^-|-$/.test(s)) return 'An address cannot start or end with a hyphen.';
  if (reserved.includes(s)) return `“${s}” is a platform route and cannot be an address.`;
  if (taken.includes(s)) return 'Another business already has that address.';
  return '';
}

/** The first free variant of a suggestion: lykke, lykke-2, lykke-3 … */
export function freeSlug(name, opts = {}) {
  const base = slugify(name) || 'business';
  if (!slugError(base, opts)) return base;
  for (let n = 2; n < 50; n++) {
    const tried = `${base}-${n}`.slice(0, 64);
    if (!slugError(tried, opts)) return tried;
  }
  return '';
}

/* ── The four fields ──────────────────────────────────────────────────── */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * What is wrong with the form, field by field. An empty object is a form that
 * can be submitted.
 *
 * @param {object} form  { name, industry, phone, email, slug }
 * @param {object} opts  { taken, reserved, industries }
 */
export function problems(form = {}, opts = {}) {
  const out = {};
  const name = String(form.name || '').trim();
  if (!name) out.name = 'What is the business called?';
  else if (name.length < 2) out.name = 'That is too short to be a name.';
  else if (name.length > 80) out.name = 'That is too long for a name.';

  if (!form.industry) out.industry = 'Pick the trade you are in.';
  else if (opts.industries && !opts.industries.includes(form.industry))
    out.industry = 'That trade is not on Avtale.net yet.';

  if (!String(form.phone || '').trim()) out.phone = 'A mobile number, so we can reach you.';
  else if (!validPhone(form.phone)) out.phone = 'We need a Norwegian mobile number.';

  const email = String(form.email || '').trim();
  if (!email) out.email = 'An email address for the account.';
  else if (!EMAIL.test(email)) out.email = 'That does not look like an email address.';

  const bad = slugError(form.slug, opts);
  if (bad) out.slug = bad;

  return out;
}

export const ready = (form, opts) => Object.keys(problems(form, opts)).length === 0;

/** The form, cleaned, in the shape the adapter takes. */
export function clean(form = {}) {
  return {
    name: String(form.name || '').trim(),
    industry: form.industry || null,
    phone: normalise(form.phone),
    email: String(form.email || '').trim().toLowerCase(),
    slug: String(form.slug || '').toLowerCase(),
  };
}

/* ── The organisation number ──────────────────────────────────────────────

   Nine digits with a mod-11 check digit. Checking it rejects a typo and
   nothing more: it does not say the number exists, it does not say it belongs
   to this business, and it is NEVER generated. Every screen that takes one
   says in as many words that it has not been looked up in
   Brønnøysundregistrene, because a green tick that only means "the arithmetic
   works" is a green tick that will be read as "verified". */

const WEIGHTS = [3, 2, 7, 6, 5, 4, 3, 2];

export function orgError(value) {
  const d = String(value || '').replace(/\s/g, '');
  if (!d) return 'The organisation number is needed before the page can go public.';
  if (!/^\d{9}$/.test(d)) return 'An organisation number is nine digits.';
  const sum = WEIGHTS.reduce((n, w, i) => n + w * Number(d[i]), 0);
  const rest = sum % 11;
  const check = rest === 0 ? 0 : 11 - rest;
  /* 10 is not a digit, so a number whose arithmetic lands there cannot exist.
     Brønnøysund simply does not issue one. */
  if (check === 10 || check !== Number(d[8]))
    return 'That is nine digits, but not a valid organisation number — check it.';
  return '';
}

/**
 * Is this a number that could have been issued at all?
 *
 * What it is FOR: the demonstration catalogue carries thirty-four
 * organisation numbers and thirty-three of them fail this check — they were
 * invented by the prototype, and they are printed on every tenant page and
 * quoted in every engagement document. A string that fails mod-11 was never
 * issued by Brønnøysundregistrene, so it belongs to nobody; it only looks
 * like a number.
 *
 * So the surfaces ask before printing one as a fact, and label it where it
 * does not check out — the same way every other demonstration figure on this
 * platform is labelled. What this function does NOT do is say the number is
 * registered, or registered to THIS business: that needs Brønnøysund, and
 * nothing here talks to them.
 */
export const orgOk = value => orgError(value) === '';

/** "917442002" → "917 442 002". The way it is printed on everything else. */
export const orgWords = value => {
  const d = String(value || '').replace(/\s/g, '');
  return /^\d{9}$/.test(d) ? `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}` : String(value || '');
};

/* ── Before the page is public ────────────────────────────────────────────

   A business exists from the moment it signs up. Its page does not: an
   address, a telephone number and an org.no are what make the page a page
   rather than a heading over an empty column, and the engagement document
   quotes the org.no by law.

   `missing` is a list of sentences, in the order they should be done. It is
   what the console shows the day after sign-up, and it is the honest answer
   to "why is my page not up yet". */

export const NEEDED = [
  { id: 'org_no', say: 'Your organisation number', why: 'It goes on your page and on every agreement.' },
  { id: 'street', say: 'Where you are', why: 'The address customers come to, and what puts you on a map.' },
  { id: 'phone', say: 'A telephone number for customers', why: 'Printed on the page and on confirmations.' },
  { id: 'opening_hours', say: 'Your opening hours', why: 'The booking calendar is built from them.' },
  { id: 'services', say: 'At least one thing you do', why: 'There is nothing to book without it.' },
  /* A booking is made with a person, not with a building — `confirmBooking`
     assigns one off the roster and an appointment that cannot say who it is
     with is an appointment with a doorway. A sole trader is one row here. */
  { id: 'staff', say: 'At least one person who does it', why: 'Every appointment is booked with somebody.' },
];

const LISTS = ['services', 'staff'];

export function missing(business = {}) {
  return NEEDED.filter(n => (LISTS.includes(n.id)
    ? !(business[n.id] || []).length
    : !String(business[n.id] || '').trim()));
}

export const publishable = business => missing(business).length === 0;

/** One sentence for the banner, rather than a list nobody reads on a phone. */
export function missingWords(business) {
  const left = missing(business);
  if (!left.length) return 'Your page is live.';
  const names = left.map(n => n.say.replace(/^Your |^A |^At least one /i, '').toLowerCase());
  const last = names.pop();
  return `Still needed before your page goes public: ${
    names.length ? names.join(', ') + ' and ' + last : last}.`;
}
